import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Instagram, RefreshCw, Plug, AlertTriangle, CheckCircle2, Loader2, Trash2, ExternalLink, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminInstagram() {
  const [config, setConfig] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    try {
      const [cfg, m] = await Promise.all([
        api.get("/admin/instagram/config"),
        api.get("/admin/instagram/media?limit=200"),
      ]);
      setConfig(cfg.data);
      setMedia(m.data.data || []);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/instagram/sync");
      toast.success(`Synced ${data.fetched} posts (${data.total_cached} cached)`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const refreshToken = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/instagram/refresh");
      toast.success(`Token refreshed — ${data.days} days until next refresh`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Refresh failed (check META_APP_ID/SECRET env vars)");
    } finally {
      setRefreshing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? Cached posts stay, but the access token is wiped. You'll need to paste a fresh one to sync again.")) return;
    try {
      await api.delete("/admin/instagram/config");
      toast.success("Disconnected");
      await refresh();
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const togglePlacement = async (igId, placement, value) => {
    const optimisticMedia = media.map((m) =>
      m.ig_id === igId ? { ...m, placements: { ...m.placements, [placement]: value } } : m
    );
    setMedia(optimisticMedia);
    try {
      const { data } = await api.patch(`/admin/instagram/media/${igId}`, { [placement]: value });
      setMedia((cur) => cur.map((m) => (m.ig_id === igId ? { ...m, ...data } : m)));
    } catch (err) {
      toast.error("Failed to update — reverting");
      await refresh();
    }
  };

  const hideMedia = async (igId) => {
    if (!window.confirm("Hide this post permanently? It won't reappear after re-sync.")) return;
    try {
      await api.delete(`/admin/instagram/media/${igId}`);
      toast.success("Hidden");
      await refresh();
    } catch {
      toast.error("Failed to hide");
    }
  };

  const counts = {
    total: media.length,
    landing: media.filter((m) => m.placements?.landing).length,
    social_page: media.filter((m) => m.placements?.social_page).length,
    footer: media.filter((m) => m.placements?.footer).length,
  };

  if (loading) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-instagram-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display uppercase text-4xl text-white flex items-center gap-3">
            <Instagram className="h-8 w-8 text-[#FF331F]" /> Instagram
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Connect your IG, sync posts, curate which ones appear on the site.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {config?.connected ? (
            <>
              <Button onClick={sync} disabled={syncing} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest" data-testid="ig-sync-btn">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Sync now
              </Button>
              <Button onClick={refreshToken} disabled={refreshing} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest" data-testid="ig-refresh-token-btn">
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh token
              </Button>
              <Button onClick={() => setShowConnect(true)} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest">
                Update token
              </Button>
              <Button onClick={disconnect} variant="ghost" className="text-red-500 hover:text-red-400 font-bold uppercase tracking-widest">
                <Trash2 className="h-4 w-4 mr-2" /> Disconnect
              </Button>
            </>
          ) : (
            <Button onClick={() => setShowConnect(true)} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="ig-connect-btn">
              <Plug className="h-4 w-4 mr-2" /> Connect Instagram
            </Button>
          )}
        </div>
      </div>

      {/* Status panel */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <StatusCard
          label="Connection"
          value={config?.connected ? `@${config?.username || "ig_account"}` : "Not connected"}
          accent={config?.connected ? "ok" : "warn"}
          icon={config?.connected ? CheckCircle2 : AlertTriangle}
          testid="ig-status-connection"
        />
        <StatusCard
          label="Token expires"
          value={config?.days_remaining != null ? `${config.days_remaining} days` : "—"}
          accent={config?.days_remaining != null && config.days_remaining < 10 ? "warn" : "neutral"}
          testid="ig-status-expiry"
        />
        <StatusCard
          label="Cached posts"
          value={`${config?.media_count ?? 0}`}
          accent="neutral"
          testid="ig-status-cache"
        />
        <StatusCard
          label="Last synced"
          value={config?.last_sync_at ? new Date(config.last_sync_at).toLocaleString() : "Never"}
          accent={config?.last_sync_error ? "warn" : "neutral"}
          testid="ig-status-sync"
        />
      </div>

      {config?.last_sync_error && (
        <div className="border border-amber-700 bg-amber-950/30 text-amber-200 p-4 mb-6 text-sm">
          <AlertTriangle className="h-4 w-4 inline mr-2" /> Last sync error: {config.last_sync_error}
        </div>
      )}

      {/* Curation tabs */}
      {config?.connected ? (
        <Tabs defaultValue="all">
          <TabsList className="bg-[#0F0F0F] border border-[#27272A] rounded-sm">
            <TabsTrigger value="all" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">All ({counts.total})</TabsTrigger>
            <TabsTrigger value="landing" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">Landing ({counts.landing})</TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">Social page ({counts.social_page})</TabsTrigger>
            <TabsTrigger value="footer" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">Footer ({counts.footer})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <MediaGrid media={media} togglePlacement={togglePlacement} hideMedia={hideMedia} />
          </TabsContent>
          <TabsContent value="landing" className="mt-6">
            <MediaGrid media={media.filter((m) => m.placements?.landing)} togglePlacement={togglePlacement} hideMedia={hideMedia} />
          </TabsContent>
          <TabsContent value="social" className="mt-6">
            <MediaGrid media={media.filter((m) => m.placements?.social_page)} togglePlacement={togglePlacement} hideMedia={hideMedia} />
          </TabsContent>
          <TabsContent value="footer" className="mt-6">
            <MediaGrid media={media.filter((m) => m.placements?.footer)} togglePlacement={togglePlacement} hideMedia={hideMedia} />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="border border-[#27272A] bg-[#0F0F0F] p-10 text-center">
          <Instagram className="h-12 w-12 text-[#FF331F] mx-auto mb-4" />
          <h2 className="font-display uppercase text-2xl text-white mb-2">Connect Instagram to start</h2>
          <p className="text-zinc-400 text-sm mb-6 max-w-xl mx-auto">
            Once connected, your posts will sync into this admin and you can choose which ones appear on the Landing page, the <code>/social</code> page, and the footer strip.
          </p>
          <Button onClick={() => setShowConnect(true)} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="ig-connect-cta">
            <Plug className="h-4 w-4 mr-2" /> Connect Instagram
          </Button>
        </div>
      )}

      <ConnectDialog open={showConnect} onOpenChange={setShowConnect} current={config} onSaved={refresh} />
    </div>
  );
}

function StatusCard({ label, value, accent = "neutral", icon: Icon, testid }) {
  const border =
    accent === "ok" ? "border-emerald-800/60" :
    accent === "warn" ? "border-amber-700/60" : "border-[#27272A]";
  const text =
    accent === "ok" ? "text-emerald-300" :
    accent === "warn" ? "text-amber-300" : "text-white";
  return (
    <div className={`border ${border} bg-[#0F0F0F] p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{label}</div>
      <div className={`font-display uppercase text-xl ${text} flex items-center gap-2`}>
        {Icon && <Icon className="h-4 w-4" />} {value}
      </div>
    </div>
  );
}

function MediaGrid({ media, togglePlacement, hideMedia }) {
  if (!media.length) {
    return <div className="border border-[#27272A] bg-[#0F0F0F] p-8 text-zinc-500 text-sm text-center">No posts in this view.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {media.map((m) => (
        <div key={m.ig_id} className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden flex flex-col" data-testid={`ig-media-${m.ig_id}`}>
          <a href={m.permalink} target="_blank" rel="noreferrer" className="block aspect-square bg-[#050505] overflow-hidden relative group">
            <img src={m.thumbnail_url || m.media_url} alt={m.caption?.slice(0, 80) || "Instagram post"} className="w-full h-full object-cover" loading="lazy" />
            {m.media_type === "VIDEO" && (
              <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5">Video</span>
            )}
            {m.media_type === "CAROUSEL_ALBUM" && (
              <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5">Album</span>
            )}
            <ExternalLink className="absolute top-2 left-2 h-3 w-3 text-white/0 group-hover:text-white/80 transition-colors" />
          </a>
          <div className="p-3 flex-1 flex flex-col gap-2">
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{m.timestamp ? new Date(m.timestamp).toLocaleDateString() : ""}</div>
            <div className="text-xs text-zinc-300 line-clamp-2 min-h-[2.5rem]">{m.caption || <span className="text-zinc-600 italic">(no caption)</span>}</div>
            <div className="border-t border-[#27272A] pt-2 mt-auto space-y-1.5">
              <PlacementToggle label="Landing" checked={!!m.placements?.landing} onChange={(v) => togglePlacement(m.ig_id, "landing", v)} testid={`ig-toggle-landing-${m.ig_id}`} />
              <PlacementToggle label="Social page" checked={!!m.placements?.social_page} onChange={(v) => togglePlacement(m.ig_id, "social_page", v)} testid={`ig-toggle-social-${m.ig_id}`} />
              <PlacementToggle label="Footer" checked={!!m.placements?.footer} onChange={(v) => togglePlacement(m.ig_id, "footer", v)} testid={`ig-toggle-footer-${m.ig_id}`} />
            </div>
            <button onClick={() => hideMedia(m.ig_id)} className="text-[10px] text-zinc-600 hover:text-red-500 uppercase tracking-widest flex items-center gap-1 mt-1">
              <EyeOff className="h-3 w-3" /> Hide
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlacementToggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#FF331F]" data-testid={testid} />
    </div>
  );
}

function ConnectDialog({ open, onOpenChange, current, onSaved }) {
  const [form, setForm] = useState({ access_token: "", ig_user_id: "", username: "", page_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ access_token: "", ig_user_id: current?.ig_user_id || "", username: current?.username || "", page_id: current?.page_id || "" });
  }, [open, current]);

  const save = async () => {
    if (!form.access_token || !form.ig_user_id) {
      toast.error("Paste both the access token and the Instagram User ID");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/instagram/config", form);
      toast.success("Saved. Hit 'Sync now' to pull posts.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2"><Plug className="h-5 w-5 text-[#FF331F]" /> Connect Instagram</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            Paste your long-lived access token + Instagram User ID
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Long-lived access token *</Label>
            <Input value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder="EAAGm0PX4ZCpsBA..." className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs mt-1" data-testid="ig-input-token" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Instagram User ID *</Label>
              <Input value={form.ig_user_id} onChange={(e) => setForm({ ...form, ig_user_id: e.target.value })} placeholder="17841400000000000" className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs mt-1" data-testid="ig-input-userid" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Username (for display)</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="flow_state_method" className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Facebook Page ID (optional)</Label>
            <Input value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} placeholder="123456789012345" className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs mt-1" />
          </div>
          <div className="bg-[#050505] border-l-2 border-[#FF331F] p-4 text-xs text-zinc-400 leading-relaxed">
            <strong className="text-zinc-200">How to get these:</strong>
            <ol className="list-decimal pl-4 mt-2 space-y-1">
              <li>Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">Graph API Explorer</a>.</li>
              <li>Select your Meta app · request scopes <code>instagram_basic, pages_show_list, pages_read_engagement</code> · click <em>Generate Access Token</em>.</li>
              <li>Use the <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">Token Debugger</a> to <em>Extend Access Token</em> → that&apos;s your 60-day long-lived token.</li>
              <li>In Graph API Explorer, run <code className="text-[10px]">GET /me/accounts</code> → copy your Page ID, then <code className="text-[10px]">GET /{`{page-id}`}?fields=instagram_business_account</code> → that returns your Instagram User ID.</li>
              <li>Paste both above and hit Save.</li>
            </ol>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[#27272A] pt-4">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="ig-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
